/**
 * Subscription client wrapper around WebSocketClient
 * Provides high-level subscription methods for real-time data
 */

import { WebSocketClient } from "../../transport/websocket/WebSocketClient";
import { fromSatoshi } from "../../utils/math";
import {
  SubscriptionClientConfig,
  SubscriptionCallback,
  ConnectionState,
} from "../../types";
import { MultiplexStreamClient } from "./MultiplexStreamClient";

export class SubscriptionClient {
  private wsClient: WebSocketClient;
  private subscriptions: Map<string, SubscriptionCallback[]> = new Map();
  private lastPriceWrappedCallbacks: Map<
    SubscriptionCallback,
    SubscriptionCallback
  > = new Map();
  private wrappedCallbacks: Map<
    string,
    Map<SubscriptionCallback, SubscriptionCallback>
  > = new Map();
  private multiplexStreamClient: MultiplexStreamClient;
  /** Multi-feed sub-channel subscriptions (generic subscribe by channel name) */
  private multiFeedSubscriptions: Map<string, SubscriptionCallback[]> =
    new Map();
  private multiFeedWrappedCallbacks: Map<
    string,
    Map<SubscriptionCallback, SubscriptionCallback>
  > = new Map();

  constructor(config: SubscriptionClientConfig) {
    this.wsClient = new WebSocketClient({
      url: config.url,
      authToken: config.authToken,
      walletAddress: config.walletAddress,
      deviceID: config.deviceID,
    });
    this.multiplexStreamClient = new MultiplexStreamClient(this.wsClient);
  }

  /**
   * Connect to WebSocket server
   */
  async connect(): Promise<void> {
    await this.wsClient.connect();
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    this.wsClient.disconnect();
    this.subscriptions.clear();
    this.lastPriceWrappedCallbacks.clear();
    this.wrappedCallbacks.clear();
    this.multiFeedSubscriptions.clear();
    this.multiFeedWrappedCallbacks.clear();
    this.multiplexStreamClient.detach();
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.wsClient.isConnected();
  }

  /**
   * Get connection state
   */
  getState(): ConnectionState {
    return this.wsClient.getState();
  }

  /**
   * Register a callback for connection state changes
   * Returns an unsubscribe function
   */
  onStateChange(handler: (state: ConnectionState) => void): () => void {
    return this.wsClient.onStateChange(handler);
  }

  /**
   * Set authentication token, address, and device ID
   * Mirrors SetAuthToken from ws_manager.go
   * Delegates to WebSocketClient.setAuthToken
   */
  setAuthToken(token: string, address: string, deviceID: string): void {
    this.wsClient.setAuthToken(token, address, deviceID);
  }

  /**
   * Update authentication credentials
   * Now async to await reconnection
   * Delegates to WebSocketClient.updateAuth
   */
  async updateAuth(
    authToken?: string,
    walletAddress?: string,
    deviceID?: string,
  ): Promise<void> {
    return this.wsClient.updateAuth(authToken, walletAddress, deviceID);
  }

  /**
   * Check if ready for subscriptions (connected and authenticated if required)
   * Delegates to WebSocketClient.isReadyForSubscriptions
   */
  isReadyForSubscriptions(): boolean {
    return this.wsClient.isReadyForSubscriptions();
  }

  /**
   * Get current authentication state
   * Delegates to WebSocketClient.getAuthState
   */
  getAuthState(): "pending" | "authenticated" | "failed" | "not_required" {
    return this.wsClient.getAuthState();
  }

  /**
   * Wait for subscription acknowledgment
   * Delegates to WebSocketClient.waitForSubscription
   *
   * @param channel - Channel name to wait for
   * @param timeoutMs - Timeout in milliseconds (default: 5000)
   */
  async waitForSubscription(
    channel: string,
    timeoutMs: number = 5000,
  ): Promise<void> {
    return this.wsClient.waitForSubscription(channel, timeoutMs);
  }

  /**
   * Get the WebSocketClient instance
   * This allows other clients (like SubscriptionExplorer) to share the same WebSocket connection
   * @returns The WebSocketClient instance
   */
  getWebSocketClient(): WebSocketClient {
    return this.wsClient;
  }

  /**
   * Helper to parse router data field (can be JSON string or object)
   */
  private parseRouterData(data: unknown): unknown {
    if (typeof data === "string") {
      try {
        return JSON.parse(data) as unknown;
      } catch (e) {
        console.error("Failed to parse router data field:", e);
        return null;
      }
    }
    return data;
  }

  /**
   * Helper to extract and parse data from response
   * Supports both action-based protocol (primary) and Router protocol (backward compatibility)
   */
  private extractRouterData(message: unknown): unknown | null {
    if (message == null || typeof message !== "object") return null;
    const m = message as Record<string, unknown>;
    // Handle action-based protocol: { action: "data" | "update", channel: "...", data: {...} } or direct data
    if (typeof m.action === "string") {
      if (
        (m.action === "data" || m.action === "update") &&
        m.data !== undefined
      ) {
        const parsed = this.parseRouterData(m.data);
        if (parsed == null) {
          console.warn(
            "[SubscriptionClient] Failed to parse action-based data:",
            m.data,
          );
        }
        return parsed ?? null;
      }
      if (m.action === "data" || m.action === "update") {
        const { action: _a, channel: _c, ...data } = m;
        return Object.keys(data).length > 0 ? data : null;
      }
      if (m.action === "error") {
        if (m.error !== undefined) console.error("WebSocket error response:", m.error);
        else if (typeof m.message === "string") console.error("WebSocket error response:", m.message);
        return null;
      }
      if (m.action === "subscribed") return null;
    }

    if (m.type === "data" && m.data !== undefined) {
      const parsed = this.parseRouterData(m.data);
      if (parsed == null) {
        console.warn("[SubscriptionClient] Failed to parse router data:", m.data);
      }
      return parsed ?? null;
    }
    if (m.type === "error") {
      if (m.error !== undefined) console.error("WebSocket error response:", m.error);
      else if (typeof m.message === "string") console.error("WebSocket error response:", m.message);
      return null;
    }
    if (m.type === "ack") return null;

    if (m.channel !== undefined && m.action === undefined && m.type === undefined) {
      const { channel: _ch, ...data } = m;
      return Object.keys(data).length > 0 ? data : null;
    }

    if (
      typeof m.action === "string" &&
      m.action !== "data" &&
      m.action !== "update" &&
      m.action !== "subscribed" &&
      m.action !== "error"
    ) {
      console.debug("[SubscriptionClient] Unexpected action:", m.action, "Full message:", message);
    } else if (
      typeof m.type === "string" &&
      m.type !== "data" &&
      m.type !== "ack" &&
      m.type !== "error"
    ) {
      console.debug("[SubscriptionClient] Unexpected message type:", m.type, "Full message:", message);
    } else if (
      (m.action === "data" || m.action === "update" || m.type === "data") &&
      m.data === undefined &&
      m.channel === undefined
    ) {
      console.warn("[SubscriptionClient] Message is data/update but missing data/channel field:", message);
    }
    return null;
  }

  /**
   * Parameter validation methods
   */
  private validateMarketId(marketId: string): boolean {
    if (marketId === null || marketId === undefined) {
      return false;
    }
    // Ensure it's a string
    const marketIdStr = String(marketId).trim();
    // Check it's not empty and not "0" (which might be invalid)
    // Note: "0" might be valid for some markets, but typically we want non-zero market IDs
    return marketIdStr !== "";
  }

  private validateKlineInterval(interval: string): boolean {
    const validIntervals = ["1m", "5m", "15m", "1h", "4h", "1d"];
    return validIntervals.includes(interval);
  }

  private validateTWAPWindowSize(windowSize: number): boolean {
    return (
      typeof windowSize === "number" &&
      windowSize > 0 &&
      Number.isFinite(windowSize)
    );
  }

  private validatePrivateChannelAuth(): void {
    // Check if ready for subscriptions (connected and authenticated if required)
    if (!this.isReadyForSubscriptions()) {
      const authState = this.getAuthState();
      if (authState === "pending") {
        throw new Error(
          "Authentication is pending. Please wait for authentication to complete before subscribing to private channels.",
        );
      } else if (authState === "failed") {
        throw new Error(
          "Authentication failed. Please re-authenticate before subscribing to private channels.",
        );
      } else {
        if (!this.wsClient.getAuthToken()) {
          throw new Error(
            "Authentication token is required for private channels. Please provide authToken in SubscriptionClient config.",
          );
        }
      }
    }
  }

  /**
   * Get the MultiplexStreamClient for the unified multiplex WebSocket API (channel "multiplex").
   * Use for streams: price_feed, markPrice, orderbook, trades, positions, transactions, etc.
   */
  getMultiplexStreamClient(): MultiplexStreamClient {
    return this.multiplexStreamClient;
  }

  /**
   * Subscribe to a multi-feed sub-channel by name (generic API used by hooks).
   * Sends subscribe with channel = subChannel and params = { market_id, ...params }.
   * Used for: buckets, kline, orders, positions, funding_rate, deviation, twap, etc.
   */
  subscribeMultiFeedSubChannel(
    subChannel: string,
    marketId: string,
    callback: SubscriptionCallback,
    params?: Record<string, unknown>,
  ): void {
    if (!this.isConnected()) {
      throw new Error("WebSocket not connected. Call connect() first.");
    }
    if (!this.validateMarketId(marketId)) {
      throw new Error(
        `Invalid market ID: ${marketId}. Market ID must be a non-empty string.`,
      );
    }
    const privateChannels = ["buckets", "orders", "positions"];
    if (privateChannels.includes(subChannel)) {
      this.validatePrivateChannelAuth();
    }

    this.multiFeedSubscriptions.set(subChannel, [
      ...(this.multiFeedSubscriptions.get(subChannel) || []),
      callback,
    ]);

    const wrappedCallback = (message: unknown) => {
      const data = this.extractRouterData(message);
      if (data != null) {
        callback(data);
      }
    };

    if (!this.multiFeedWrappedCallbacks.has(subChannel)) {
      this.multiFeedWrappedCallbacks.set(subChannel, new Map());
    }
    this.multiFeedWrappedCallbacks.get(subChannel)!.set(callback, wrappedCallback);

    this.wsClient.on(subChannel, wrappedCallback);

    const authToken = this.wsClient.getAuthToken();
    const sendParams: Record<string, unknown> = {
      market_id: marketId,
      ...params,
    };
    if (
      privateChannels.includes(subChannel) &&
      authToken &&
      !sendParams.auth_token
    ) {
      sendParams.auth_token = authToken;
    }

    try {
      this.wsClient.send({
        type: "subscribe",
        channel: subChannel,
        params: sendParams,
      });
    } catch (error) {
      this.multiFeedWrappedCallbacks.get(subChannel)?.delete(callback);
      const callbacks = this.multiFeedSubscriptions.get(subChannel) || [];
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.multiFeedSubscriptions.set(subChannel, callbacks);
      }
      throw error;
    }
  }

  /**
   * Unsubscribe from a multi-feed sub-channel. If callback is omitted, removes all callbacks for that subChannel.
   */
  unsubscribeMultiFeedSubChannel(
    subChannel: string,
    callback?: SubscriptionCallback,
  ): void {
    const callbacks = this.multiFeedSubscriptions.get(subChannel) || [];

    if (callback) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.multiFeedSubscriptions.set(subChannel, callbacks);

        const wrappedCallback = this.multiFeedWrappedCallbacks
          .get(subChannel)
          ?.get(callback);
        if (wrappedCallback) {
          this.wsClient.off(subChannel, wrappedCallback);
          this.multiFeedWrappedCallbacks.get(subChannel)!.delete(callback);
        }
      }
    } else {
      const channelWrappedCallbacks = this.multiFeedWrappedCallbacks.get(subChannel);
      if (channelWrappedCallbacks) {
        channelWrappedCallbacks.forEach((wrappedCallback) => {
          this.wsClient.off(subChannel, wrappedCallback);
        });
        this.multiFeedWrappedCallbacks.delete(subChannel);
      }
      this.multiFeedSubscriptions.delete(subChannel);
    }

    if (
      (this.multiFeedSubscriptions.get(subChannel)?.length ?? 0) === 0 &&
      this.isConnected()
    ) {
      try {
        this.wsClient.send({
          type: "unsubscribe",
          channel: subChannel,
        });
      } catch (error) {
        console.error("Failed to send unsubscribe message:", error);
      }
    }
  }

  /**
   * ============================================================================
   * Individual Channel Subscription Methods
   * ============================================================================
   */

  /**
   * Subscribe to ticker updates (moniker channel)
   * Note: Router uses "moniker" channel name, but we keep "ticker" in the API for consistency
   */
  subscribeTicker(marketId: string, callback: SubscriptionCallback): void {
    if (!this.isConnected()) {
      throw new Error("WebSocket not connected. Call connect() first.");
    }

    // Validate parameters
    if (!this.validateMarketId(marketId)) {
      throw new Error(
        `Invalid market ID: ${marketId}. Market ID must be a non-empty string.`,
      );
    }

    // Router protocol uses "moniker" channel name, but we track as "ticker" internally
    const routerChannel = "moniker";
    const internalChannel = "ticker";
    this.subscriptions.set(internalChannel, [
      ...(this.subscriptions.get(internalChannel) || []),
      callback,
    ]);

    // Wrap callback to handle Router protocol format
    // WebSocketClient now routes type="data" messages by channel name, so we can register directly on channel
    const wrappedCallback = (message: unknown) => {
      const tickerData = this.extractRouterData(message);
      if (tickerData != null) {
        callback(tickerData);
      }
    };

    // Store wrapped callback for proper cleanup
    if (!this.wrappedCallbacks.has(internalChannel)) {
      this.wrappedCallbacks.set(internalChannel, new Map());
    }
    this.wrappedCallbacks.get(internalChannel)!.set(callback, wrappedCallback);

    // Register handler on router channel name (WebSocketClient routes type="data" messages by channel)
    this.wsClient.on(routerChannel, wrappedCallback);

    // Send subscription message using Router protocol format
    try {
      this.wsClient.send({
        type: "subscribe",
        channel: routerChannel,
        params: {
          market_id: marketId,
        },
      });
    } catch (error) {
      // Clean up on send failure
      this.wrappedCallbacks.get(internalChannel)?.delete(callback);
      const callbacks = this.subscriptions.get(internalChannel) || [];
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(internalChannel, callbacks);
      }
      throw error;
    }
  }

  /**
   * Unsubscribe from ticker updates
   */
  unsubscribeTicker(_marketId: string, callback?: SubscriptionCallback): void {
    const internalChannel = "ticker";
    const routerChannel = "moniker";
    const callbacks = this.subscriptions.get(internalChannel) || [];

    if (callback) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(internalChannel, callbacks);

        // Remove wrapped callback
        const wrappedCallback = this.wrappedCallbacks
          .get(internalChannel)
          ?.get(callback);
        if (wrappedCallback) {
          this.wsClient.off(routerChannel, wrappedCallback);
          this.wrappedCallbacks.get(internalChannel)!.delete(callback);
        }
      }
    } else {
      // Remove all callbacks
      const channelWrappedCallbacks =
        this.wrappedCallbacks.get(internalChannel);
      if (channelWrappedCallbacks) {
        channelWrappedCallbacks.forEach((wrappedCallback) => {
          this.wsClient.off(routerChannel, wrappedCallback);
        });
        this.wrappedCallbacks.delete(internalChannel);
      }
      this.subscriptions.delete(internalChannel);
    }

    if (
      this.subscriptions.get(internalChannel)?.length === 0 &&
      this.isConnected()
    ) {
      try {
        this.wsClient.send({
          type: "unsubscribe",
          channel: routerChannel,
        });
      } catch (error) {
        console.error("Failed to send unsubscribe message:", error);
      }
    }
  }

  /**
   * Subscribe to last price updates
   * @param marketId - Market ID to subscribe to (string or number)
   * @param callback - Callback function for last price updates
   * @param convertToDecimal - Whether to convert satoshi to decimal (default: true)
   */
  subscribeLastPrice(
    marketId: string,
    callback: SubscriptionCallback,
    convertToDecimal: boolean = true,
  ): void {
    if (!this.isConnected()) {
      throw new Error("WebSocket not connected. Call connect() first.");
    }

    // Validate parameters
    if (!this.validateMarketId(marketId)) {
      throw new Error(
        `Invalid market ID: ${marketId}. Market ID must be a non-empty string.`,
      );
    }

    const channel = "lastPrice";
    this.subscriptions.set(channel, [
      ...(this.subscriptions.get(channel) || []),
      callback,
    ]);

    const wrappedCallback = (message: unknown) => {
      const raw = this.extractRouterData(message);
      if (raw == null || typeof raw !== "object") {
        console.warn("[SubscriptionClient] Failed to extract trade data from message:", message);
        return;
      }
      const tradeData = raw as Record<string, unknown>;
      if (tradeData.price === undefined || tradeData.price === null) {
        console.warn("[SubscriptionClient] Trade data missing price field:", tradeData);
        return;
      }
      if (convertToDecimal) {
        if (tradeData.price != null) {
          tradeData.price = fromSatoshi(
            typeof tradeData.price === "string" ? tradeData.price : String(tradeData.price),
          );
        }
        if (tradeData.quantity != null) {
          tradeData.quantity = fromSatoshi(
            typeof tradeData.quantity === "string" ? tradeData.quantity : String(tradeData.quantity),
          );
        }
      }
      callback(tradeData);
    };

    // Store wrapped callback for proper cleanup
    this.lastPriceWrappedCallbacks.set(callback, wrappedCallback);

    // Register handler on channel name (WebSocketClient routes type="data" messages by channel)
    this.wsClient.on(channel, wrappedCallback);

    // Send subscription message using Router protocol format
    try {
      this.wsClient.send({
        type: "subscribe",
        channel: "lastPrice",
        params: {
          market_id: marketId,
        },
      });
    } catch (error) {
      // Clean up on send failure
      this.lastPriceWrappedCallbacks.delete(callback);
      const callbacks = this.subscriptions.get(channel) || [];
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);
      }
      throw error;
    }
  }

  /**
   * Unsubscribe from last price updates
   */
  unsubscribeLastPrice(
    _marketId: string,
    callback?: SubscriptionCallback,
  ): void {
    const channel = "lastPrice";
    const callbacks = this.subscriptions.get(channel) || [];

    if (callback) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);

        // Remove wrapped callback
        const wrappedCallback = this.lastPriceWrappedCallbacks.get(callback);
        if (wrappedCallback) {
          this.wsClient.off(channel, wrappedCallback);
          this.lastPriceWrappedCallbacks.delete(callback);
        }
      }
    } else {
      // Remove all callbacks
      callbacks.forEach((cb) => {
        const wrappedCallback = this.lastPriceWrappedCallbacks.get(cb);
        if (wrappedCallback) {
          this.wsClient.off(channel, wrappedCallback);
          this.lastPriceWrappedCallbacks.delete(cb);
        }
      });
      this.subscriptions.delete(channel);
    }

    // Send unsubscribe message if no more callbacks and connected
    if (this.subscriptions.get(channel)?.length === 0 && this.isConnected()) {
      try {
        this.wsClient.send({
          type: "unsubscribe",
          channel: "lastPrice",
        });
      } catch (error) {
        console.error("Failed to send unsubscribe message:", error);
      }
    }
  }

  /**
   * Subscribe to buckets (user's margin buckets)
   * Requires authentication (walletAddress must be provided)
   * @param callback - Callback function for buckets updates
   */
  subscribeBuckets(callback: SubscriptionCallback): void {
    if (!this.isConnected()) {
      throw new Error("WebSocket not connected. Call connect() first.");
    }

    // Validate authentication for private channel
    this.validatePrivateChannelAuth();

    const channel = "buckets";
    this.subscriptions.set(channel, [
      ...(this.subscriptions.get(channel) || []),
      callback,
    ]);

    const wrappedCallback = (message: unknown) => {
      const bucketsData = this.extractRouterData(message);
      if (bucketsData != null) {
        callback(bucketsData);
      }
    };

    if (!this.wrappedCallbacks.has(channel)) {
      this.wrappedCallbacks.set(channel, new Map());
    }
    this.wrappedCallbacks.get(channel)!.set(callback, wrappedCallback);

    this.wsClient.on(channel, wrappedCallback);

    // Register pending subscription for acknowledgment tracking
    this.wsClient.registerPendingSubscription(channel);

    const authToken = this.wsClient.getAuthToken();
    try {
      const params: Record<string, unknown> = {};
      if (authToken) params.auth_token = authToken;
      this.wsClient.send({
        type: "subscribe",
        channel: "buckets",
        params,
      });
      // Optionally wait for subscription acknowledgment:
      // await this.waitForSubscription(channel);
    } catch (error) {
      this.wrappedCallbacks.get(channel)?.delete(callback);
      const callbacks = this.subscriptions.get(channel) || [];
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);
      }
      throw error;
    }
  }

  /**
   * Unsubscribe from buckets updates
   */
  unsubscribeBuckets(callback?: SubscriptionCallback): void {
    const channel = "buckets";
    const callbacks = this.subscriptions.get(channel) || [];

    if (callback) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);

        const wrappedCallback = this.wrappedCallbacks
          .get(channel)
          ?.get(callback);
        if (wrappedCallback) {
          this.wsClient.off(channel, wrappedCallback);
          this.wrappedCallbacks.get(channel)!.delete(callback);
        }
      }
    } else {
      const channelWrappedCallbacks = this.wrappedCallbacks.get(channel);
      if (channelWrappedCallbacks) {
        channelWrappedCallbacks.forEach((wrappedCallback) => {
          this.wsClient.off(channel, wrappedCallback);
        });
        this.wrappedCallbacks.delete(channel);
      }
      this.subscriptions.delete(channel);
    }

    if (this.subscriptions.get(channel)?.length === 0 && this.isConnected()) {
      try {
        this.wsClient.send({
          type: "unsubscribe",
          channel: "buckets",
        });
      } catch (error) {
        console.error("Failed to send unsubscribe message:", error);
      }
    }
  }

  /**
   * Subscribe to kline (OHLC candlestick) updates
   * @param marketId - Market ID to subscribe to (string or number)
   * @param interval - Interval for kline data (required: "1m", "5m", "15m", "1h", "4h", "1d")
   * @param callback - Callback function for kline updates
   */
  subscribeKline(
    marketId: string,
    interval: string,
    callback: SubscriptionCallback,
  ): void {
    if (!this.isConnected()) {
      throw new Error("WebSocket not connected. Call connect() first.");
    }

    // Validate parameters
    if (!this.validateMarketId(marketId)) {
      throw new Error(
        `Invalid market ID: ${marketId}. Market ID must be a non-empty string.`,
      );
    }
    if (!this.validateKlineInterval(interval)) {
      throw new Error(
        `Invalid interval: ${interval}. Interval must be one of: "1m", "5m", "15m", "1h", "4h", "1d".`,
      );
    }

    const channel = "kline";
    this.subscriptions.set(channel, [
      ...(this.subscriptions.get(channel) || []),
      callback,
    ]);

    const wrappedCallback = (message: unknown) => {
      const klineData = this.extractRouterData(message);
      if (klineData) {
        callback(klineData);
      }
    };

    if (!this.wrappedCallbacks.has(channel)) {
      this.wrappedCallbacks.set(channel, new Map());
    }
    this.wrappedCallbacks.get(channel)!.set(callback, wrappedCallback);

    this.wsClient.on(channel, wrappedCallback);

    // Send subscription message using Router protocol format
    try {
      this.wsClient.send({
        type: "subscribe",
        channel: "kline",
        params: {
          market_id: marketId,
          interval: interval,
        },
      });
    } catch (error) {
      this.wrappedCallbacks.get(channel)?.delete(callback);
      const callbacks = this.subscriptions.get(channel) || [];
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);
      }
      throw error;
    }
  }

  /**
   * Unsubscribe from kline updates
   */
  unsubscribeKline(
    _marketId: string,
    _interval: string,
    callback?: SubscriptionCallback,
  ): void {
    const channel = "kline";
    const callbacks = this.subscriptions.get(channel) || [];

    if (callback) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);

        const wrappedCallback = this.wrappedCallbacks
          .get(channel)
          ?.get(callback);
        if (wrappedCallback) {
          this.wsClient.off(channel, wrappedCallback);
          this.wrappedCallbacks.get(channel)!.delete(callback);
        }
      }
    } else {
      const channelWrappedCallbacks = this.wrappedCallbacks.get(channel);
      if (channelWrappedCallbacks) {
        channelWrappedCallbacks.forEach((wrappedCallback) => {
          this.wsClient.off(channel, wrappedCallback);
        });
        this.wrappedCallbacks.delete(channel);
      }
      this.subscriptions.delete(channel);
    }

    if (this.subscriptions.get(channel)?.length === 0 && this.isConnected()) {
      try {
        this.wsClient.send({
          type: "unsubscribe",
          channel: "kline",
        });
      } catch (error) {
        console.error("Failed to send unsubscribe message:", error);
      }
    }
  }

  /**
   * Subscribe to TWAP (Time-Weighted Average Price) updates
   * @param marketId - Market ID to subscribe to (string or number)
   * @param windowSize - Time window in seconds (e.g., 300 for 5 minutes)
   * @param callback - Callback function for TWAP updates
   */
  subscribeTWAP(
    marketId: string,
    windowSize: number,
    callback: SubscriptionCallback,
  ): void {
    if (!this.isConnected()) {
      throw new Error("WebSocket not connected. Call connect() first.");
    }

    // Validate parameters
    if (!this.validateMarketId(marketId)) {
      throw new Error(
        `Invalid market ID: ${marketId}. Market ID must be a non-empty string.`,
      );
    }
    if (!this.validateTWAPWindowSize(windowSize)) {
      throw new Error(
        `Invalid window size: ${windowSize}. Window size must be a positive number.`,
      );
    }

    const channel = "twap";
    this.subscriptions.set(channel, [
      ...(this.subscriptions.get(channel) || []),
      callback,
    ]);

    const wrappedCallback = (message: unknown) => {
      const twapData = this.extractRouterData(message);
      if (twapData) {
        callback(twapData);
      }
    };

    if (!this.wrappedCallbacks.has(channel)) {
      this.wrappedCallbacks.set(channel, new Map());
    }
    this.wrappedCallbacks.get(channel)!.set(callback, wrappedCallback);

    this.wsClient.on(channel, wrappedCallback);

    // Send subscription message using Router protocol format
    try {
      this.wsClient.send({
        type: "subscribe",
        channel: "twap",
        params: {
          market_id: marketId,
          window_size: windowSize,
        },
      });
    } catch (error) {
      this.wrappedCallbacks.get(channel)?.delete(callback);
      const callbacks = this.subscriptions.get(channel) || [];
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);
      }
      throw error;
    }
  }

  /**
   * Unsubscribe from TWAP updates
   */
  unsubscribeTWAP(
    _marketId: string,
    _windowSize: number,
    callback?: SubscriptionCallback,
  ): void {
    const channel = "twap";
    const callbacks = this.subscriptions.get(channel) || [];

    if (callback) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);

        const wrappedCallback = this.wrappedCallbacks
          .get(channel)
          ?.get(callback);
        if (wrappedCallback) {
          this.wsClient.off(channel, wrappedCallback);
          this.wrappedCallbacks.get(channel)!.delete(callback);
        }
      }
    } else {
      const channelWrappedCallbacks = this.wrappedCallbacks.get(channel);
      if (channelWrappedCallbacks) {
        channelWrappedCallbacks.forEach((wrappedCallback) => {
          this.wsClient.off(channel, wrappedCallback);
        });
        this.wrappedCallbacks.delete(channel);
      }
      this.subscriptions.delete(channel);
    }

    if (this.subscriptions.get(channel)?.length === 0 && this.isConnected()) {
      try {
        this.wsClient.send({
          type: "unsubscribe",
          channel: "twap",
        });
      } catch (error) {
        console.error("Failed to send unsubscribe message:", error);
      }
    }
  }

  /**
   * Subscribe to balances updates (authenticated channel)
   * Requires authentication (walletAddress must be provided)
   * @param callback - Callback function for balances updates
   */
  subscribeBalances(callback: SubscriptionCallback): void {
    if (!this.isConnected()) {
      throw new Error("WebSocket not connected. Call connect() first.");
    }

    // Validate authentication for private channel
    this.validatePrivateChannelAuth();

    const channel = "balances";
    this.subscriptions.set(channel, [
      ...(this.subscriptions.get(channel) || []),
      callback,
    ]);

    const wrappedCallback = (message: unknown) => {
      const balancesData = this.extractRouterData(message);
      if (balancesData) {
        callback(balancesData);
      }
    };

    if (!this.wrappedCallbacks.has(channel)) {
      this.wrappedCallbacks.set(channel, new Map());
    }
    this.wrappedCallbacks.get(channel)!.set(callback, wrappedCallback);

    this.wsClient.on(channel, wrappedCallback);

    // Register pending subscription for acknowledgment tracking
    this.wsClient.registerPendingSubscription(channel);

    const authToken = this.wsClient.getAuthToken();
    try {
      const params: Record<string, unknown> = {};
      if (authToken) params.auth_token = authToken;
      this.wsClient.send({
        type: "subscribe",
        channel: "balances",
        params,
      });
      // Optionally wait for subscription acknowledgment:
      // await this.waitForSubscription(channel);
    } catch (error) {
      this.wrappedCallbacks.get(channel)?.delete(callback);
      const callbacks = this.subscriptions.get(channel) || [];
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);
      }
      throw error;
    }
  }

  /**
   * Unsubscribe from balances updates
   */
  unsubscribeBalances(callback?: SubscriptionCallback): void {
    const channel = "balances";
    const callbacks = this.subscriptions.get(channel) || [];

    if (callback) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);

        const wrappedCallback = this.wrappedCallbacks
          .get(channel)
          ?.get(callback);
        if (wrappedCallback) {
          this.wsClient.off(channel, wrappedCallback);
          this.wrappedCallbacks.get(channel)!.delete(callback);
        }
      }
    } else {
      const channelWrappedCallbacks = this.wrappedCallbacks.get(channel);
      if (channelWrappedCallbacks) {
        channelWrappedCallbacks.forEach((wrappedCallback) => {
          this.wsClient.off(channel, wrappedCallback);
        });
        this.wrappedCallbacks.delete(channel);
      }
      this.subscriptions.delete(channel);
    }

    if (this.subscriptions.get(channel)?.length === 0 && this.isConnected()) {
      try {
        this.wsClient.send({
          type: "unsubscribe",
          channel: "balances",
        });
      } catch (error) {
        console.error("Failed to send unsubscribe message:", error);
      }
    }
  }

  /**
   * Subscribe to orders updates (authenticated channel)
   * Requires authentication (walletAddress must be provided)
   * @param callback - Callback function for orders updates
   */
  subscribeOrders(callback: SubscriptionCallback): void {
    if (!this.isConnected()) {
      throw new Error("WebSocket not connected. Call connect() first.");
    }

    // Validate authentication for private channel
    this.validatePrivateChannelAuth();

    const channel = "orders";
    this.subscriptions.set(channel, [
      ...(this.subscriptions.get(channel) || []),
      callback,
    ]);

    const wrappedCallback = (message: unknown) => {
      const ordersData = this.extractRouterData(message);
      if (ordersData) {
        callback(ordersData);
      }
    };

    if (!this.wrappedCallbacks.has(channel)) {
      this.wrappedCallbacks.set(channel, new Map());
    }
    this.wrappedCallbacks.get(channel)!.set(callback, wrappedCallback);

    this.wsClient.on(channel, wrappedCallback);

    // Register pending subscription for acknowledgment tracking
    this.wsClient.registerPendingSubscription(channel);

    const authToken = this.wsClient.getAuthToken();
    try {
      const params: Record<string, unknown> = {};
      if (authToken) params.auth_token = authToken;
      this.wsClient.send({
        type: "subscribe",
        channel: "orders",
        params,
      });
      // Optionally wait for subscription acknowledgment:
      // await this.waitForSubscription(channel);
    } catch (error) {
      this.wrappedCallbacks.get(channel)?.delete(callback);
      const callbacks = this.subscriptions.get(channel) || [];
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);
      }
      throw error;
    }
  }

  /**
   * Unsubscribe from orders updates
   */
  unsubscribeOrders(callback?: SubscriptionCallback): void {
    const channel = "orders";
    const callbacks = this.subscriptions.get(channel) || [];

    if (callback) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);

        const wrappedCallback = this.wrappedCallbacks
          .get(channel)
          ?.get(callback);
        if (wrappedCallback) {
          this.wsClient.off(channel, wrappedCallback);
          this.wrappedCallbacks.get(channel)!.delete(callback);
        }
      }
    } else {
      const channelWrappedCallbacks = this.wrappedCallbacks.get(channel);
      if (channelWrappedCallbacks) {
        channelWrappedCallbacks.forEach((wrappedCallback) => {
          this.wsClient.off(channel, wrappedCallback);
        });
        this.wrappedCallbacks.delete(channel);
      }
      this.subscriptions.delete(channel);
    }

    if (this.subscriptions.get(channel)?.length === 0 && this.isConnected()) {
      try {
        this.wsClient.send({
          type: "unsubscribe",
          channel: "orders",
        });
      } catch (error) {
        console.error("Failed to send unsubscribe message:", error);
      }
    }
  }
}
